/**
 * "New client" modal — creates a prospect manually,
 * optionally approving it for registration immediately.
 */

import { useState } from "react";
import ModalCloseButton from "../../../../components/ui/ModalCloseButton";
import { useToast } from "../../../../contexts/ToastContext";
import {
  useCreateProspectMutation,
  useApproveProspectRegistrationMutation,
} from "../../../../store/services/nivApi";
import { PROSPECT_SOURCE_LABELS } from "../../../../services/nivFirebase";

const ISRAELI_MOBILE_REGEX = /^(05\d{8}|\+9725\d{8})$/;

const rtkErrorMessage = (error) =>
  error?.message || error?.data?.message || "אירעה שגיאה, נסה שוב";

function ProspectFormModal({ author, onClose, onCreated }) {
  const { showSuccess, showError } = useToast();
  const [createProspect, { isLoading: isCreating }] = useCreateProspectMutation();
  const [approveRegistration, { isLoading: isApproving }] =
    useApproveProspectRegistrationMutation();

  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [email, setEmail] = useState("");
  const [source, setSource] = useState("manual");
  const [initialNote, setInitialNote] = useState("");
  const [approveNow, setApproveNow] = useState(false);
  const [validationError, setValidationError] = useState("");

  const isSubmitting = isCreating || isApproving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = phone.replace(/[\s-]/g, "");
    if (!ISRAELI_MOBILE_REGEX.test(cleanPhone)) {
      setValidationError("יש להזין מספר נייד ישראלי תקין (05XXXXXXXX)");
      return;
    }
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      setValidationError("כתובת האימייל אינה תקינה");
      return;
    }
    setValidationError("");

    try {
      const created = await createProspect({
        phone: cleanPhone,
        fullName: fullName.trim(),
        clinicName: clinicName.trim(),
        email: email.trim(),
        source,
        initialNote: initialNote.trim(),
        author,
      }).unwrap();

      if (approveNow) {
        await approveRegistration({
          phone: created.id,
          note: initialNote.trim() || null,
          addedByName: author,
        }).unwrap();
        showSuccess("הליד נוצר ואושר להרשמה 🎉");
      } else {
        showSuccess("הליד נוצר בהצלחה");
      }
      onCreated?.(created.id);
    } catch (error) {
      console.error("[CK-9995] Error creating prospect from form:", {
        errorCode: "CK-9995",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError(rtkErrorMessage(error));
    }
  };

  return (
    <div
      className="nivcl-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="nivcl-modal" role="dialog" aria-modal="true">
        <ModalCloseButton onClick={onClose} />
        <h3 className="nivcl-modal-title">ליד חדש</h3>

        <form className="nivcl-fields" onSubmit={handleSubmit}>
          <label className="nivcl-field">
            <span>
              טלפון נייד <span className="nivcl-required">*</span>
            </span>
            <input
              type="tel"
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="05X-XXXXXXX"
              required
              autoFocus
            />
          </label>

          <label className="nivcl-field">
            <span>שם מלא</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="שם הליד"
            />
          </label>

          <label className="nivcl-field">
            <span>שם הקליניקה</span>
            <input
              type="text"
              value={clinicName}
              onChange={(e) => setClinicName(e.target.value)}
              placeholder="שם הקליניקה"
            />
          </label>

          <label className="nivcl-field">
            <span>אימייל</span>
            <input
              type="email"
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </label>

          <label className="nivcl-field">
            <span>מקור</span>
            <select value={source} onChange={(e) => setSource(e.target.value)}>
              {Object.entries(PROSPECT_SOURCE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label className="nivcl-field">
            <span>הערה ראשונית</span>
            <textarea
              value={initialNote}
              onChange={(e) => setInitialNote(e.target.value)}
              placeholder="פרטים על השיחה, צרכים, הקשר…"
              rows={3}
            />
          </label>

          <label className="nivcl-checkbox">
            <input
              type="checkbox"
              checked={approveNow}
              onChange={(e) => setApproveNow(e.target.checked)}
            />
            <span>אשר להרשמה מיד</span>
          </label>

          {validationError && (
            <p className="nivcl-form-error" role="alert">
              {validationError}
            </p>
          )}

          <div className="nivcl-modal-actions">
            <button
              type="submit"
              className="nivcl-primary-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "שומר…" : "צור לקוח"}
            </button>
            <button type="button" className="nivcl-ghost-btn" onClick={onClose}>
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProspectFormModal;
